import {
  CreateReminderDtoNotificationChannelsEnum,
  CreateReminderDtoRecurrenceTypeEnum,
} from 'woofwoof-api';
import type { MedicationsFormModalValuesProps } from 'src/sections/@dashboard/profile/components/medication-form/MedicationFormModal';
import { buildDosingPayload } from 'src/sections/@dashboard/profile/components/medication-form/medicationFormPayload';
import { buildStockEntry } from 'src/sections/@dashboard/profile/components/medication-form/medicationStockValues';

/**
 * Transform the medication form's flat reminder fields into the nested shape
 * the visit-scoped BE endpoints expect (`reminderCustomRecurrence` + channels).
 * Used by VisitCourseCard and VisitModalActivityTab to submit add/update.
 *
 * Every Facility medication screen sends this payload, so the reminder
 * travels in the request the write series freezes (R4-3). Only the owner's
 * profile page still chains its reminder separately.
 */
export function buildMedicationSubmitPayload(
  formData: MedicationsFormModalValuesProps,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    medicineProductId: formData.medicineProductId,
    name: formData.name,
    preparationType: formData.preparationType,
    dosage: formData.dosage,
    dosageInterval: formData.dosageInterval,
    notes: formData.notes,
    reminderEnabled: formData.reminderEnabled,
    ...buildDosingPayload(formData),
    ...buildStockEntry(formData),
  };

  if (!formData.reminderEnabled) return base;

  const channels: CreateReminderDtoNotificationChannelsEnum[] = [];
  if (formData.reminderEmailEnabled)
    channels.push(CreateReminderDtoNotificationChannelsEnum.Email);
  if (formData.reminderSmsEnabled)
    channels.push(CreateReminderDtoNotificationChannelsEnum.Sms);
  if (channels.length === 0)
    channels.push(CreateReminderDtoNotificationChannelsEnum.Email);

  const scheduledAt = formData.reminderScheduledAt
    ? new Date(formData.reminderScheduledAt).toISOString()
    : undefined;

  base.reminderDescription = formData.reminderDescription;
  base.reminderScheduledAt = scheduledAt;
  base.reminderRecurrenceType = formData.reminderRecurrenceType;
  base.reminderNotificationChannels = channels;

  if (
    formData.reminderRecurrenceType ===
    CreateReminderDtoRecurrenceTypeEnum.Custom
  ) {
    base.reminderCustomRecurrence = {
      every: formData.reminderCustomEvery,
      unit: formData.reminderCustomUnit,
      ...(formData.reminderCustomDayOfWeek !== ''
        ? { dayOfWeek: Number(formData.reminderCustomDayOfWeek) }
        : {}),
      ...(formData.reminderCustomWeekOfMonth !== ''
        ? { weekOfMonth: formData.reminderCustomWeekOfMonth }
        : {}),
    };
  }

  return base;
}
